<template>
  <AppModal v-model="show" title="Markup Calculator">

    <template #body>
      <div class="mt-4">
        <!-- Cost -->
        <BaseInputCurrency
          v-model="cost"
          name="cost"
          type="number"
          label="Cost ($)"
          @input="handleInputCost"
        />

        <!-- Markup -->
        <BaseInput
          v-model="markup"
          name="markup"
          type="number"
          label="Markup (%)"
          @input="handleInputMarkup"
        />

        <!-- Revenue -->
        <BaseInputCurrency
          v-model="revenue"
          name="revenue"
          type="number"
          label="Revenue ($)"
          @input="handleInputRevenue"
        />

        <!-- Profit -->
        <BaseInputCurrency
          v-model="profit"
          name="profit"
          type="number"
          label="Profit ($)"
          @input="handleInputProfit"
        />
      </div>
    </template>

    <!-- Use this calculation button  -->
    <template #footer>
      <hr class="my-4">
      <div class="flex justify-end gap-2 my-3 mr-4 sm:mr-6">
        <BaseButton
          variant="secondary"
          text="Cancel"
          @click="show = false"
        />
        <BaseButton
          variant="primary"
          text="Use Calculation"
          :disabled="!revenue"
          @click="useCalculation"
        />
      </div>
    </template>
  </AppModal>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import AppModal from '@/components/app/AppModal.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseInputCurrency from './base/BaseInputCurrency.vue'
import { toNumber } from '@/utils'

const props = defineProps({
  initialValue: {
    type: [Number, String],
    default: 0
  }
})

const show = defineModel({
  default: false
})

const emit = defineEmits(['use-calculation'])

const cost = ref<number | null>(null)
const markup = ref<number | null>(null)
const revenue = ref<number | null>(null)
const profit = ref<number | null>(null)
const lastInput = ref<string>('')
const from = ref<string>('')
const currentInput = ref<string>('')
const calculationUsed = ref<boolean>(false)
const initialCost = ref<number>(0)
const lastRevenue = ref<number | null>(null)

const resetFields = () => {
  cost.value = initialCost.value
  markup.value = null
  revenue.value = null
  profit.value = null
  lastInput.value = cost.value ? 'cost' : ''
  currentInput.value = ''
  from.value = ''
  calculationUsed.value = false
  lastRevenue.value = null
}

const parseNumber = (value: number) => {
  if (isNaN(value)) {
    return null
  } else {
    return parseFloat(value.toFixed(2))
  }
}

watch(() => props.initialValue, (value) => {
  if (!calculationUsed.value) {
    initialCost.value = toNumber(value)
    resetFields()
    return
  }
  if (value !== lastRevenue.value) {
    initialCost.value = toNumber(value)
    resetFields()
  }
})

const calculateCostToMarkup = () => {
  const costValue = cost.value ? toNumber(cost.value) : 0
  const markupValue = markup.value ? toNumber(markup.value) : 0

  const revenueValue = costValue * (1 + markupValue / 100)
  const profitValue = revenueValue - costValue

  revenue.value = parseNumber(revenueValue)
  profit.value = parseNumber(profitValue)
}

const calculateRevenueToCost = () => {
  const revenueValue = revenue.value ? toNumber(revenue.value) : 0
  const costValue = cost.value ? toNumber(cost.value) : 0

  const markupValue = (revenueValue - costValue) / costValue * 100
  const profitValue = revenueValue - costValue

  markup.value = parseNumber(markupValue)
  profit.value = parseNumber(profitValue)
}

const calculateCostToProfit = () => {
  const costValue = cost.value ? toNumber(cost.value) : 0
  const profitValue = profit.value ? toNumber(profit.value) : 0

  const revenueValue = costValue + profitValue
  const markupValue = profitValue / costValue * 100

  revenue.value = parseNumber(revenueValue)
  markup.value = parseNumber(markupValue)
}

const calculateMarkupToRevenue = () => {
  const markupValue = markup.value ? toNumber(markup.value) : 0
  const revenueValue = revenue.value ? toNumber(revenue.value) : 0

  const costValue = revenueValue / (1 + markupValue / 100)
  const profitValue = revenueValue - costValue

  cost.value = parseNumber(costValue)
  profit.value = parseNumber(profitValue)
}

const calculateMarkupToProfit = () => {
  const markupValue = markup.value ? toNumber(markup.value) : 0
  const profitValue = profit.value ? toNumber(profit.value) : 0

  const costValue = profitValue / (markupValue / 100)
  const revenueValue = costValue + profitValue

  cost.value = parseNumber(costValue)
  revenue.value = parseNumber(revenueValue)
}

const calculateRevenueToProfit = () => {
  const revenueValue = revenue.value ? toNumber(revenue.value) : 0
  const profitValue = profit.value ? toNumber(profit.value) : 0

  const costValue = revenueValue - profitValue
  const markupValue = (revenueValue - costValue) / costValue * 100

  cost.value = parseNumber(costValue)
  markup.value = parseNumber(markupValue)
}

const handleInputCost = () => {
  currentInput.value = 'cost'
  if (lastInput.value === 'markup') {
    calculateCostToMarkup()
    from.value = 'markup'
  } else if (lastInput.value === 'revenue') {
    calculateRevenueToCost()
    from.value = 'revenue'
  } else if (lastInput.value === 'profit') {
    calculateCostToProfit()
    from.value = 'profit'
  } else {
    if (from.value) {
      if (from.value === 'markup') {
        calculateCostToMarkup()
      } else if (from.value === 'revenue') {
        calculateRevenueToCost()
      } else if (from.value === 'profit') {
        calculateCostToProfit()
      }
    }
  }

  lastInput.value = 'cost'
}

const handleInputMarkup = () => {
  currentInput.value = 'markup'
  if (lastInput.value === 'cost') {
    calculateCostToMarkup()
    from.value = 'cost'
  } else if (lastInput.value === 'revenue') {
    calculateMarkupToRevenue()
    from.value = 'revenue'
  } else if (lastInput.value === 'profit') {
    calculateMarkupToProfit()
    from.value = 'profit'
  } else {
    if (from.value) {
      if (from.value === 'cost') {
        calculateCostToMarkup()
      } else if (from.value === 'revenue') {
        calculateMarkupToRevenue()
      } else if (from.value === 'profit') {
        calculateMarkupToProfit()
      }
    }
  }

  lastInput.value = 'markup'
}

const handleInputRevenue = () => {
  currentInput.value = 'revenue'
  if (lastInput.value === 'cost') {
    calculateRevenueToCost()
    from.value = 'cost'
  } else if (lastInput.value === 'markup') {
    calculateMarkupToRevenue()
    from.value = 'markup'
  } else if (lastInput.value === 'profit') {
    calculateRevenueToProfit()
    from.value = 'profit'
  } else {
    if (from.value) {
      if (from.value === 'cost') {
        calculateRevenueToCost()
      } else if (from.value === 'markup') {
        calculateMarkupToRevenue()
      } else if (from.value === 'profit') {
        calculateRevenueToProfit()
      }
    }
  }

  lastInput.value = 'revenue'
}

const handleInputProfit = () => {
  currentInput.value = 'profit'
  if (lastInput.value === 'cost') {
    calculateCostToProfit()
    from.value = 'cost'
  } else if (lastInput.value === 'markup') {
    calculateMarkupToProfit()
    from.value = 'markup'
  } else if (lastInput.value === 'revenue') {
    calculateRevenueToProfit()
    from.value = 'revenue'
  } else {
    if (from.value) {
      if (from.value === 'cost') {
        calculateCostToProfit()
      } else if (from.value === 'markup') {
        calculateMarkupToProfit()
      } else if (from.value === 'revenue') {
        calculateRevenueToProfit()
      }
    }
  }

  lastInput.value = 'profit'
}

const useCalculation = () => {
  lastRevenue.value = revenue.value
  calculationUsed.value = true
  emit('use-calculation', revenue.value)
  show.value = false
}

onMounted(() => {
  resetFields()
})
</script>
